import type { App } from 'vue'
import {
  createRouter,
  createWebHistory,
  type NavigationGuardReturn,
  type RouteLocationNormalized,
  type Router
} from 'vue-router'
import axios from 'axios'
import { useRouteGuards } from '@fusion/auth-v3'
import { createFusionRoutes, type FusionRoutesOptions } from '@fusion/saas-components-v3'
import PublicHome from '@/views/PublicHome.vue'
import PrivateHome from '@/views/PrivateHome.vue'
import ReleaseNotes from '@/views/ReleaseNotes.vue'
import NonVerified from '@/views/NonVerified.vue'
import HelpCenter from '@/views/HelpCenter.vue'

let router: Router

/**
 * Register all navigation components required to properly render the router.
 * @param app The Vue application instance.
 * @returns
 */
const registerComponents = (app: App) => {
  app.component('HelpCenter', HelpCenter)
  app.component('PublicHome', PublicHome)
  app.component('PrivateHome', PrivateHome)
  app.component('ReleaseNotes', ReleaseNotes)
  app.component('NonVerified', NonVerified)
}

const guardFuncs = {
  TestRouteGuard: (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized
  ): NavigationGuardReturn => {
    if (from.path === '/colors' && to.path === '/route-guard') {
      return true
    } else {
      return '/'
    }
  }
}

/**
 * Gets the router dynamically using data from the database.
 * @param app The Vue application instance.
 * @returns The Vue Router instance
 */
const initializeRouter = async (app: App): Promise<Router> => {
  const applicationId = APP_ENV.VITE_APP_APPLICATION_ID
  const result = await axios.get(
    `${APP_ENV.VITE_APP_APPLICATIONS_HOST}/applications/${applicationId}/navigation`
  )
  const { status, data, statusText } = result

  if (status >= 200 && status <= 299) {
    registerComponents(app)
    const options: FusionRoutesOptions = {
      app,
      navRoutes: data,
      authGuards: useRouteGuards(),
      guardFuncs
    }
    router = createRouter({
      history: createWebHistory(APP_ENV.VITE_APP_PUBLIC_PATH),
      routes: createFusionRoutes(options)
    })
  } else {
    console.error('Failed to load navigation routes: ', statusText)
  }

  return router
}

/**
 * Ensure the router is created only once.
 * @param app The Vue application instance.
 * @returns The Vue Router instance
 */
export const getRouter = async (app: App): Promise<Router> => {
  if (router) {
    return router
  }
  return initializeRouter(app)
}
